import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";

// The account menu (opened from the avatar), shared by the normal header and the POS header:
// who you are, a prominent How-to Guide card, your account links in two columns, and the
// Change Password / Logout buttons at the bottom.
const useStyles = makeStyles({
  root: { width: "min(340px, calc(100vw - 24px))", maxHeight: "calc(100vh - 84px)", overflowY: "auto", background: "#fff" },
  superRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "calc(100% - 20px)",
    margin: "4px 10px 4px",
    padding: "6px 10px",
    border: "1px solid #ddd6fe",
    borderRadius: 12,
    background: "#f5f3ff",
    color: "#6d28d9",
    fontSize: "0.86rem",
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "left",
    transition: "background .12s, border-color .12s",
    "&:hover": { background: "#ede9fe", borderColor: "#a78bfa" },
  },
  profile: { display: "flex", alignItems: "center", gap: 12, padding: "9px 14px", background: "linear-gradient(135deg,#fff7ed,#ffedd5)" },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#ea580c,#f59e0b)",
    color: "#fff",
    fontWeight: 800,
    fontSize: "0.85rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  name: { fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  role: { display: "inline-block", marginTop: 4, padding: "1px 8px", borderRadius: 999, background: "#fff", border: "1px solid #fed7aa", color: "#c2410c", fontSize: "0.68rem", fontWeight: 800, letterSpacing: ".03em" },
  sub: { fontSize: "0.76rem", color: "#64748b", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  guide: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "calc(100% - 20px)",
    margin: "8px 10px 2px",
    padding: "7px 10px",
    border: "1px solid #fdba74",
    borderRadius: 14,
    background: "linear-gradient(135deg,#fff7ed,#fff)",
    cursor: "pointer",
    textAlign: "left",
    transition: "box-shadow .15s, transform .15s, border-color .15s",
    "&:hover": { boxShadow: "0 6px 16px -8px rgba(234,88,12,.55)", borderColor: "#ea580c", transform: "translateY(-1px)" },
    "&:focus-visible": { outline: "2px solid #ea580c", outlineOffset: 2 },
  },
  guideIcon: { width: 30, height: 30, borderRadius: 9, background: "#ea580c", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  guideTitle: { fontSize: "0.92rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 },
  guideSub: { fontSize: "0.74rem", color: "#64748b", marginTop: 1 },
  label: { fontSize: "0.68rem", fontWeight: 800, letterSpacing: ".07em", color: "#94a3b8", textTransform: "uppercase", padding: "7px 16px 1px" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, padding: "0 8px 2px" },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "5px 8px",
    border: 0,
    borderRadius: 10,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#334155",
    minWidth: 0,
    transition: "background .12s, color .12s",
    "&:hover": { background: "#fff7ed", color: "#ea580c" },
    "&:focus-visible": { outline: "2px solid #ea580c", outlineOffset: -2 },
    "& span": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  },
  footer: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "8px 10px 10px", borderTop: "1px solid #f1f5f9", marginTop: 2 },
  pill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "#fff",
    cursor: "pointer",
    fontSize: "0.84rem",
    fontWeight: 700,
    color: "#334155",
    transition: "background .12s, border-color .12s",
    "&:hover": { background: "#f8fafc", borderColor: "#cbd5e1" },
  },
  logout: { color: "#dc2626", borderColor: "#fecaca", "&:hover": { background: "#fef2f2", borderColor: "#fca5a5" } },
});

const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "U";

/**
 * items: [{ key, label, icon (element), onClick, color }] - the account links shown in two columns.
 */
const AccountMenuPanel = React.forwardRef(({ user, sections, onSuperAdmin, onGuide, onChangePassword, onLogout, onKeyDown }, ref) => {
  const classes = useStyles();
  const result = user?.result || {};
  const rawRole = Array.isArray(result.userType) ? result.userType[0] : result.userType || result.role || "";
  const prettyRole = String(rawRole).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
  const squash = (v) => String(v || "").toLowerCase().replace(/\s+/g, "");
  const roleLabel = prettyRole && squash(prettyRole) !== squash(result.name) ? prettyRole : null;
  return (
    <div ref={ref} className={classes.root} onKeyDown={onKeyDown} role="menu">
      <div className={classes.profile}>
        <div className={classes.avatar}>{initials(result.name)}</div>
        <div style={{ minWidth: 0 }}>
          <div className={classes.name}>{result.name}</div>
          <div className={classes.sub}>{result.email || "Signed in"}</div>
          {roleLabel && <span className={classes.role}>{roleLabel}</span>}
        </div>
      </div>

      <button type="button" className={classes.guide} onClick={onGuide} role="menuitem" autoFocus>
        <span className={classes.guideIcon}>
          <MenuBookIcon fontSize="small" />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.guideTitle}>How-to Guide</div>
          <div className={classes.guideSub}>Step-by-step help for the whole app</div>
        </span>
        <ChevronRightIcon style={{ color: "#ea580c" }} />
      </button>

      {sections
        .filter((sec) => sec.items.length > 0)
        .map((sec) => (
          <React.Fragment key={sec.label}>
            <div className={classes.label}>{sec.label}</div>
            <div className={classes.grid}>
              {sec.items.map((it) => (
                <button key={it.key} type="button" className={classes.item} onClick={it.onClick} role="menuitem">
                  {React.cloneElement(it.icon, { style: { fontSize: 19, color: it.color || "#ea580c", flexShrink: 0 } })}
                  <span>{it.label}</span>
                </button>
              ))}
            </div>
          </React.Fragment>
        ))}

      {onSuperAdmin && (
        <button type="button" className={classes.superRow} onClick={onSuperAdmin} role="menuitem">
          <SupervisorAccountIcon style={{ fontSize: 20 }} />
          <span style={{ flex: 1 }}>Go to SuperAdmin panel</span>
          <ChevronRightIcon style={{ fontSize: 20 }} />
        </button>
      )}

      <div className={classes.footer}>
        <button type="button" className={classes.pill} onClick={onChangePassword} role="menuitem">
          <VpnKeyOutlinedIcon style={{ fontSize: 18 }} /> Password
        </button>
        <button type="button" className={`${classes.pill} ${classes.logout}`} onClick={onLogout} role="menuitem">
          <ExitToAppOutlinedIcon style={{ fontSize: 18 }} /> Logout
        </button>
      </div>
    </div>
  );
});

export default AccountMenuPanel;
